// Сетевой домен (Э2 плана docs/ПЛАН_ГРУППА_РЕСТОРАНОВ.md, п. 0.1): у папки один
// домен на всю сеть — `pinski.klait.ru`, ресторан — ПЕРВЫЙ сегмент пути:
// `pinski.klait.ru/hide/app`. Чистые правила без базы и без Next — их гоняет
// тест, а используют middleware (edge), резолв тенанта и клиент.
//
// Флаг `settings.domain_enabled` на папке: по умолчанию ВКЛЮЧЁН (Марат 18.09:
// «реальных клиентов нет, делай любые правки» — сеть всегда живёт на общем
// адресе). Выключить можно с карточки папки: тогда рестораны отвечают по своим
// сабдоменам, как одиночные.

export const NETWORK_DOMAIN_KEY = "domain_enabled";

/** Общий адрес сети включён на папке. */
export function networkDomainEnabled(settings: unknown): boolean {
  if (!settings || typeof settings !== "object") return true;
  return (settings as Record<string, unknown>)[NETWORK_DOMAIN_KEY] !== false;
}

export function withNetworkDomain(settings: unknown, enabled: boolean): Record<string, unknown> {
  const base = settings && typeof settings === "object" && !Array.isArray(settings) ? { ...(settings as Record<string, unknown>) } : {};
  return { ...base, [NETWORK_DOMAIN_KEY]: enabled };
}

/**
 * Поверхности и служебные пути — то, что стоит первым сегментом у ОДИНОЧНОГО
 * ресторана. На сетевом домене первым идёт ресторан, а поверхность — вторым;
 * этот список нужен, чтобы отличить `/hide/app` от `/app` и не принять
 * `/app` за ресторан «app».
 */
export const SURFACE_SEGMENTS = new Set([
  "app", "app2", "app3", "desk", "desk2", "tablet", "checkin", "review", "console", "mock", "login",
]);

/** Пути, которые на сетевом домене НЕ носят префикс ресторана. */
export const UNPREFIXED_SEGMENTS = new Set([
  "api", "_next", "operator", "sw.js", "manifest.webmanifest", "icon.png", "favicon.ico", "robots.txt", "icons", "offline",
  // Файлы связи с приложением (12.10): Apple и Google берут их с корня хоста
  // без редиректов — на сетевом домене префикс ресторана их бы сломал.
  ".well-known",
]);

/**
 * Хосты платформы, а не ресторана (12.10): `app.klait.ru` — общий вход по
 * логину (ресторан находит сервер), туда же ходит нативное приложение. Имена
 * заняты в `RESERVED_SLUGS`, ресторан их получить не может.
 */
export const PLATFORM_HOSTS = new Set(["app", "api"]);

/** Хост — платформенный (`app.klait.ru`, `app.localhost:3000`). */
export function isPlatformHost(host: string): boolean {
  const label = hostLabel(host);
  return label !== null && PLATFORM_HOSTS.has(label);
}

/** Страница общего входа на платформенном хосте. */
export const PLATFORM_LOGIN_PATH = "/login";

export type NetworkHosts = {
  /** slug папки → её рестораны (slug'и), только папки с включённым доменом. */
  groups: Record<string, { restaurants: string[] }>;
  /** slug ресторана → slug папки: старый адрес ресторана переводит на сетевой. */
  tenants: Record<string, string>;
};

export const EMPTY_HOSTS: NetworkHosts = { groups: {}, tenants: {} };

/** Первый ярлык хоста (`pinski` из `pinski.klait.ru:443`); голый домен/localhost → null. */
export function hostLabel(host: string): string | null {
  const h = host.split(":")[0].toLowerCase();
  const labels = h.split(".");
  if (labels.length < 2 || !labels[0] || labels[0] === "www") return null;
  return labels[0];
}

export type PathSplit = { slug: string; rest: string };

/**
 * Разобрать путь на сетевом домене: `/hide/app?x` → {slug: "hide", rest: "/app"}.
 * Первый сегмент — ресторан только если он есть в списке ресторанов сети;
 * иначе null (поверхность без ресторана, служебный путь, чужой ресторан).
 */
export function splitRestaurantPath(pathname: string, restaurants: readonly string[]): PathSplit | null {
  const m = /^\/([^/]+)(\/.*)?$/.exec(pathname);
  if (!m) return null;
  const seg = m[1].toLowerCase();
  if (!restaurants.includes(seg)) return null;
  return { slug: seg, rest: m[2] && m[2] !== "/" ? m[2] : "/app" };
}

/** Путь идёт без префикса ресторана (API, статика, консоль). */
export function isUnprefixedPath(pathname: string): boolean {
  const seg = pathname.split("?")[0].split("/")[1] ?? "";
  if (UNPREFIXED_SEGMENTS.has(seg)) return true;
  // Файлы с расширением (иконки, шрифты) — тоже служебные.
  return /\.[a-z0-9]{2,5}$/i.test(seg);
}

/**
 * Куда отправить запрос без префикса на сетевом домене: в ресторан из cookie,
 * если он ещё в сети, иначе в первый ресторан сети. Пустая сеть → null.
 */
export function pickRestaurant(restaurants: readonly string[], remembered: string | null | undefined): string | null {
  if (remembered && restaurants.includes(remembered)) return remembered;
  return restaurants[0] ?? null;
}

/** Адрес с префиксом ресторана: `/hide` + `/app?tab=x` → `/hide/app?tab=x`; `/` → `/hide/app`. */
export function prefixedPath(slug: string, pathname: string, search = ""): string {
  const p = pathname === "/" ? "/app" : pathname;
  return `/${slug}${p}${search}`;
}

/**
 * Что делать middleware с запросом.
 *  · rewrite — сетевой домен, ресторан в пути: отдать `rest`, тенант = slug;
 *  · redirect — сетевой домен без ресторана в пути, или старый адрес ресторана сети;
 *  · pass — всё остальное (одиночный ресторан, служебные пути).
 */
export type RouteDecision =
  | { kind: "pass" }
  | { kind: "rewrite"; network: string; slug: string; rest: string }
  | { kind: "redirect"; location: string; network: string; slug: string }
  | { kind: "to-login"; location: string }
  | { kind: "not-found"; reason: string };

export function decideRoute(
  input: { host: string; proto: string; pathname: string; search: string; rememberedRestaurant: string | null },
  hosts: NetworkHosts,
): RouteDecision {
  const label = hostLabel(input.host);
  if (!label) return { kind: "pass" };
  if (PLATFORM_HOSTS.has(label)) {
    // На платформенном хосте ресторана нет: страниц, кроме входа, тут не
    // открывают — `/app` без ресторана попросил бы тенанта, которого нет.
    if (isUnprefixedPath(input.pathname)) return { kind: "pass" };
    // Файлы любой глубины (`/brand/mlt-white.png`, иконки вкладки) — как есть:
    // иначе картинки страницы входа уезжали редиректом на сам вход.
    if (/\.[a-z0-9]{2,5}$/i.test(input.pathname.split("?")[0])) return { kind: "pass" };
    const seg = input.pathname.split("/")[1] ?? "";
    if (`/${seg}` === PLATFORM_LOGIN_PATH) return { kind: "pass" };
    return { kind: "to-login", location: PLATFORM_LOGIN_PATH };
  }
  const group = hosts.groups[label];
  if (group) {
    if (isUnprefixedPath(input.pathname)) return { kind: "pass" };
    const split = splitRestaurantPath(input.pathname, group.restaurants);
    if (split) return { kind: "rewrite", network: label, slug: split.slug, rest: split.rest };
    const pick = pickRestaurant(group.restaurants, input.rememberedRestaurant);
    if (!pick) return { kind: "not-found", reason: "в сети пока нет ресторанов" };
    return { kind: "redirect", location: prefixedPath(pick, input.pathname, input.search), network: label, slug: pick };
  }
  const network = hosts.tenants[label];
  if (network) {
    // Старый адрес ресторана, вошедшего в сеть: страницы переводим на сетевой
    // (п. 0.1 плана), API и статику не трогаем — установленное на телефон
    // приложение со старого адреса продолжает работать до переустановки ярлыка.
    if (isUnprefixedPath(input.pathname)) return { kind: "pass" };
    const host = input.host.split(":")[0];
    const port = input.host.includes(":") ? `:${input.host.split(":")[1]}` : "";
    const rest = host.split(".").slice(1).join(".");
    return {
      kind: "redirect",
      location: `${input.proto}://${network}.${rest}${port}${prefixedPath(label, input.pathname, input.search)}`,
      network,
      slug: label,
    };
  }
  return { kind: "pass" };
}
